import * as React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ExternalLink,
  GripVertical,
  Home,
  KeyRound,
  Mail,
  Megaphone,
  MousePointerClick,
  Plus,
  Trash2,
  Undo2,
  User as UserIcon,
} from 'lucide-react';
import type { ProfileButton } from '@proptr/shared';
import {
  NEWSLETTER_DEFAULT_SUBTITLE,
  NEWSLETTER_DEFAULT_TITLE,
} from '@proptr/shared';
import { AddProptreeButtonModal } from '../components/AddProptreeButtonModal.js';
import { NewsletterCopyFields } from '../components/NewsletterCopyFields.js';
import { ProptreeButtonRow } from '../components/ProptreeButtonRow.js';
import type {
  AgencyProptreeSettings,
  AgencyProptreeSettingsUpdateInput,
  ProptreeEnumValue,
} from '@proptr/shared';
import { ApiError } from '@proptr/api-client';
import { ImageUploadButton } from '../components/ImageUploadButton.js';
import { SectionCard } from '../components/SectionCard.js';
import { PartnerToggleList } from '../components/PartnerToggleList.js';
import { AppShell, useBannerSlot } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import {
  Select,
  type SelectOption,
} from '../components/ui/select.js';
import { Tooltip } from '../components/ui/tooltip.js';
import { isTestMode, withDemoFlag } from '../lib/testMode.js';
import { cn, initials } from '../lib/utils.js';
import { COUNTRY_CODES } from '../lib/countryCodes.js';
import { client } from '../main.js';

// All option lists below mirror the legacy
// enterprise-app/src/components/createNewProptree.js modal verbatim. The
// stored shape on the proptree doc is the legacy enum `{ id, name, value }`.

const EXTERNAL_ACCESS_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Disabled', value: 'disabled' },
  { id: 2, name: 'Enabled', value: 'enabled' },
];

const AGENCY_BANNER_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Disabled', value: 'disabled' },
  { id: 2, name: 'Enabled', value: 'enabled' },
];

const BANNER_STATS_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Show stats', value: 'enabled' },
  { id: 2, name: 'Hide stats', value: 'disabled' },
];

const MAIN_FEED_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Enabled', value: 'enabled' },
  { id: 2, name: 'Disabled', value: 'disabled' },
];

const PROPERTIES_TYPE_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Sale & Rentals', value: 'both' },
  { id: 2, name: 'Sale', value: 'sale' },
  { id: 3, name: 'Rentals', value: 'rent' },
];

const PROPERTY_PLAN_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'On-plan & Off-plan', value: 'both' },
  { id: 2, name: 'On-plan', value: 'on_plan' },
  { id: 3, name: 'Off-plan', value: 'off_plan' },
];

const AGENT_FEED_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Enabled', value: 'enabled' },
  { id: 2, name: 'Disabled', value: 'disabled' },
];

export function AgencyProptreeSettingsPage() {
  const [data, setData] = React.useState<AgencyProptreeSettings | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notFound, setNotFound] = React.useState(false);
  const [formOptions, setFormOptions] = React.useState<ProptreeEnumValue[]>([]);

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setNotFound(false);
    client.proptrees
      .getAgencyProptree()
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setError((err as Error).message);
        }
      });
    // Active forms feed the "Qualify leads" dropdown. Failures here aren't
    // fatal — the page still loads, the dropdown just shows an empty list.
    client.forms
      .list({ status: 'active', limit: 100, sort: 'name', order: 'asc' })
      .then((res) => {
        if (!alive) return;
        setFormOptions(
          res.items.map((f, i) => ({
            id: i + 1,
            name: f.name ?? '(unnamed)',
            value: f._id,
          })),
        );
      })
      .catch(() => {
        // Swallow — the select will just be empty.
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Update Agency Proptree
            </h1>
            <div className="mt-3 flex items-center gap-3">
              <Link
                to="/proptree"
                className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to proptree
              </Link>
              <span className="text-muted-foreground/50">·</span>
              <div className="eyebrow">Proptree / Agency</div>
            </div>
          </div>
          {data && (data.alias || data.shortId) && (
            <OpenProptreeCard alias={data.alias ?? data.shortId ?? ''} />
          )}
        </div>

        {error && (
          <Card className="border-destructive/40 bg-destructive/10 p-4 font-mono text-[11px] text-destructive">
            {error}
          </Card>
        )}

        {notFound && (
          <Card className="p-6">
            <p className="font-sans text-[13px] text-muted-foreground">
              No agency-default proptree exists for this organisation yet.
            </p>
          </Card>
        )}

        {!data && !error && !notFound && (
          <Card className="p-6 font-mono text-[11px] text-muted-foreground">
            Loading…
          </Card>
        )}

        {data && <SplitView data={data} formOptions={formOptions} />}
      </div>
    </AppShell>
  );
}

// 50/50 split: live preview of the proptree page on the left, form on the
// right. Stacks on smaller screens.
function SplitView({
  data,
  formOptions,
}: {
  data: AgencyProptreeSettings;
  formOptions: ProptreeEnumValue[];
}) {
  const [form, setForm] = React.useState<FormState>(() =>
    initialFormState(data),
  );
  // Last-saved snapshot. Used to compute per-section dirty state and to
  // reset to a clean slate after a save round-trip.
  const [original, setOriginal] = React.useState<FormState>(() =>
    initialFormState(data),
  );
  // Stack of previous form snapshots, in chronological order. Each user
  // edit via `set` pushes the pre-edit form onto this stack so the user
  // can step back through their changes by clicking Undo. Cleared on save
  // so we never undo across a save boundary.
  const [history, setHistory] = React.useState<FormState[]>([]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    // No-op edits (e.g. setting a value to its current value) shouldn't
    // pollute the undo stack with empty steps.
    if (JSON.stringify(form[key]) === JSON.stringify(value)) return;
    setHistory((h) => [...h, form]);
    setForm({ ...form, [key]: value });
  };

  const handleUndo = () => {
    const restored = history[history.length - 1];
    // Index access is `T | undefined` under noUncheckedIndexedAccess; the
    // length check alone doesn't narrow it.
    if (!restored) return;
    setForm(restored);
    setHistory(history.slice(0, -1));
  };
  const canUndo = history.length > 0;

  // Once the forms list arrives, reconcile the saved `feed` with an
  // actual option. Legacy stored a separate Feed-collection snapshot
  // whose id doesn't match the Form `_id`, so we match by name.
  React.useEffect(() => {
    if (formOptions.length === 0) return;
    const updater = (prev: FormState): FormState => {
      if (!data.feed) return prev;
      const alreadyMatched = formOptions.some(
        (o) => o.value === prev.feed?.value,
      );
      if (alreadyMatched) return prev;
      const byName = formOptions.find(
        (o) => (o.name ?? '').toLowerCase() === data.feed?.name.toLowerCase(),
      );
      if (!byName) return prev;
      return { ...prev, feed: byName };
    };
    setForm(updater);
    setOriginal(updater);
  }, [formOptions, data.feed]);

  const dirty: SectionDirty = {
    // `alias` is intentionally excluded — it has its own commit path
    // (the Check availability button below the input). The global save
    // banner ignores typed-but-not-checked alias edits so they don't
    // accidentally write through with the rest of the form.
    identity: isDirty(form, original, ['profileImage']),
    tagline: isDirty(form, original, ['profileFirstName']),
    buttons: isDirty(form, original, ['buttons', 'partners']),
    listings: isDirty(form, original, [
      'mainFeed',
      'propertiesType',
      'property',
      'feed',
    ]),
    marquee: isDirty(form, original, [
      'bannerSlogans',
      'showAgencyBanner',
      'agencyBannerStats',
    ]),
    newsletter: isDirty(form, original, [
      'newsletter',
      'newsletterTitle',
      'newsletterSubtitle',
    ]),
    other: isDirty(form, original, ['showAgentFeed']),
    external: isDirty(form, original, [
      'externalAccess',
      'whatsAppCountryCode',
      'whatsAppNumber',
    ]),
  };
  const anyDirty = Object.values(dirty).some(Boolean);

  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Only the newsletter card persists so far — every other field still
  // just snapshots the form as the new "original" (no save endpoint
  // yet). As more fields land, add them to `patch`. setOriginal runs
  // only after a successful PUT; the undo stack is local-only and
  // meaningless after a save.
  const handleSave = async () => {
    setSaveError(null);
    const patch: AgencyProptreeSettingsUpdateInput = {};
    if (form.newsletter !== original.newsletter) {
      patch.newsletter = form.newsletter;
    }
    // Blank copy is stored as null so the public card falls back to
    // the default text.
    if (form.newsletterTitle !== original.newsletterTitle) {
      patch.newsletterTitle = form.newsletterTitle.trim() || null;
    }
    if (form.newsletterSubtitle !== original.newsletterSubtitle) {
      patch.newsletterSubtitle = form.newsletterSubtitle.trim() || null;
    }
    if (Object.keys(patch).length > 0) {
      setSaving(true);
      try {
        await client.proptrees.updateAgencyProptree(patch);
      } catch (err) {
        setSaveError(
          err instanceof ApiError ? err.message : (err as Error).message,
        );
        return;
      } finally {
        setSaving(false);
      }
    }
    setOriginal(form);
    setHistory([]);
  };

  return (
    <div className="space-y-4">
      {anyDirty && (
        <UnsavedChangesBanner
          onSave={handleSave}
          onUndo={handleUndo}
          canUndo={canUndo}
          saving={saving}
          error={saveError}
        />
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <SettingsForm
          form={form}
          set={set}
          data={data}
          formOptions={formOptions}
          onAliasSaved={(savedAlias) => {
            // Snap original.alias to the saved value so the alias is
            // no longer dirty in the diff. Other in-flight edits stay
            // dirty and the banner remains visible for them.
            setOriginal((prev) => ({ ...prev, alias: savedAlias }));
          }}
        />
        {/* Sticky preview column. Top offset = topbar (64px) + banner
            slot height (live) + 8px breathing gap. Height fills the
            rest of the viewport minus a 16px bottom margin so the user
            can see where the preview ends. `dvh` (dynamic viewport
            units) tracks mobile address-bar collapse correctly. */}
        <div className="lg:sticky lg:top-[calc(var(--header-h,4rem)+var(--banner-h,0px)+0.5rem)] lg:h-[calc(100dvh-var(--header-h,4rem)-var(--banner-h,0px)-1.5rem)]">
          <ProptreePreview data={data} form={form} />
        </div>
      </div>
    </div>
  );
}

type SectionDirty = {
  identity: boolean;
  tagline: boolean;
  buttons: boolean;
  listings: boolean;
  marquee: boolean;
  newsletter: boolean;
  other: boolean;
  external: boolean;
};

function isDirty<K extends keyof FormState>(
  form: FormState,
  original: FormState,
  keys: K[],
): boolean {
  return keys.some(
    (k) => JSON.stringify(form[k]) !== JSON.stringify(original[k]),
  );
}

// Sticky strip below the topbar that nudges the user to save. Visible
// while any section's `dirty` flag is true. The Save button on the right
// is the same call as the per-section save bars — they all flush the
// pending diff back to the server in one shot. Portals into AppShell's
// banner slot so the yellow strip is full-bleed across the viewport and
// always sits directly under the top nav, regardless of where the calling
// component lives in the tree. The Undo button steps the form state back
// one edit at a time; click it repeatedly to walk all the way back to the
// last-saved snapshot.
function UnsavedChangesBanner({
  onSave,
  onUndo,
  canUndo,
  saving = false,
  error = null,
}: {
  onSave: () => void;
  onUndo: () => void;
  canUndo: boolean;
  saving?: boolean;
  error?: string | null;
}) {
  const slot = useBannerSlot();
  if (!slot) return null;
  return createPortal(
    <div className="border-b border-primary/40 bg-primary/10 text-foreground dark:bg-primary/20">
      <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-6 py-2 text-[13px]">
        <span className="flex-1">
          {error
            ? `Save failed: ${error}`
            : 'You have updated your page. Remember to save to reflect it on your live proptree.'}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onUndo}
          disabled={!canUndo || saving}
          title="Undo last change"
        >
          <Undo2 />
          Undo
        </Button>
        <Button type="button" size="sm" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>,
    slot,
  );
}

// Tabbed section that flips between two completely different banner
// surfaces. The top tab edits the rotating slogans (`bannerSlogans`);
// the card tab edits the legacy `showAgencyBanner` / `agencyBannerStats`
// switches. The two tabs share no controls — each shows only its own.
function MarqueeSection({
  value,
  onChange,
  showAgencyBanner,
  agencyBannerStats,
  onShowAgencyBannerChange,
  onAgencyBannerStatsChange,
  propertiesType,
  mainFeed,
  showAgentFeed,
}: {
  value: BannerSlogans;
  onChange: (next: BannerSlogans) => void;
  showAgencyBanner: ProptreeEnumValue | null;
  agencyBannerStats: ProptreeEnumValue | null;
  onShowAgencyBannerChange: (next: ProptreeEnumValue | null) => void;
  onAgencyBannerStatsChange: (next: ProptreeEnumValue | null) => void;
  // Drive which preset rows the editor should disable:
  // - propertiesType: Sale-only hides listings-for-rent, Rent-only
  //   hides listings-for-sale.
  // - mainFeed off: every listing-based preset is disabled (the
  //   listings section won't render).
  // - showAgentFeed off: the agents preset is disabled (the agents
  //   marquee won't render).
  propertiesType: ProptreeEnumValue | null;
  mainFeed: ProptreeEnumValue | null;
  showAgentFeed: ProptreeEnumValue | null;
}) {
  const [tab, setTab] = React.useState<SloganTab>('top');
  const agencyBannerEnabled = showAgencyBanner?.value === 'enabled';

  return (
    <div className="p-5">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <div className="inline-flex rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
            {(Object.keys(SLOGAN_TAB_LABELS) as SloganTab[]).map((key) => (
              <SectionTabButton
                key={key}
                active={tab === key}
                onClick={() => setTab(key)}
              >
                {SLOGAN_TAB_LABELS[key]}
              </SectionTabButton>
            ))}
          </div>
        </div>

        {tab === 'top' && (
          <>
            <div className="col-span-12 sm:col-span-6">
              <Label>Show top banner</Label>
              <SwitchBox
                checked={value.enabled}
                onChange={(v) => onChange({ ...value, enabled: v })}
                ariaLabel="Enable top section banner"
              />
              <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                Top marquee strip that rotates slogans across every page.
              </p>
            </div>
            {value.enabled && (
              <div className="col-span-12">
                <BannerSlogansEditor
                  value={value}
                  onChange={onChange}
                  propertiesType={propertiesType}
                  mainFeed={mainFeed}
                  showAgentFeed={showAgentFeed}
                />
              </div>
            )}
          </>
        )}

        {tab === 'card' && (
          <>
            <div className="col-span-12 sm:col-span-6">
              <Label>Show agency banner</Label>
              <SwitchBox
                checked={agencyBannerEnabled}
                onChange={(v) =>
                  onShowAgencyBannerChange(
                    AGENCY_BANNER_OPTIONS.find(
                      (o) => o.value === (v ? 'enabled' : 'disabled'),
                    ) ?? null,
                  )
                }
                ariaLabel="Show agency banner"
              />
              <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                Decorative card rendered between groups of listings on the
                public page.
              </p>
            </div>
            {agencyBannerEnabled && (
              <div className="col-span-12 sm:col-span-6">
                <Label>Banner stats</Label>
                <EnumSelect
                  className="mt-1 bg-muted/30"
                  options={BANNER_STATS_OPTIONS}
                  value={agencyBannerStats}
                  onChange={onAgencyBannerStatsChange}
                />
                <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Show or hide the active-listings / agents count line on
                  the agency banner.
                </p>
              </div>
            )}
            <div className="col-span-12">
              <p className="font-sans text-[12px] leading-relaxed text-muted-foreground">
                <span className="mr-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground/70">
                  OBS
                </span>
                Banner will show after the first “Load more” click, and
                only if more than 8 listings exist.
              </p>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

// "Stay in the loop" email-signup card above the public page's footer:
// on/off switch, then its title + subtitle once enabled.
function NewsletterSection({
  enabled,
  title,
  subtitle,
  onEnabledChange,
  onTitleChange,
  onSubtitleChange,
}: {
  enabled: boolean;
  title: string;
  subtitle: string;
  onEnabledChange: (v: boolean) => void;
  onTitleChange: (v: string) => void;
  onSubtitleChange: (v: string) => void;
}) {
  return (
    <div className="p-5">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 sm:col-span-6">
          <Label>Show newsletter card</Label>
          <SwitchBox
            checked={enabled}
            onChange={onEnabledChange}
            ariaLabel="Enable newsletter card"
          />
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Email-signup card shown above the footer of the public page.
          </p>
        </div>
        {enabled && (
          <NewsletterCopyFields
            idPrefix="agency-newsletter"
            title={title}
            subtitle={subtitle}
            onTitleChange={onTitleChange}
            onSubtitleChange={onSubtitleChange}
          />
        )}
      </div>
    </div>
  );
}

// Editor for one slogan surface. Two stacked panels: the four
// stat-driven presets (each a single toggle), and a list of custom
// taglines (each capped at SLOGAN_CHAR_LIMIT, list capped at
// MAX_CUSTOM_SLOGANS). Renders as a self-contained block so it can sit
// inside the Banner section's grid without further wrapping.
const SLOGAN_PRESET_LABELS: Record<keyof BannerSlogans['presets'], string> = {
  activeListings: 'Active listings count',
  agents: 'Number of agents',
  listingsForSale: 'Listings for sale',
  listingsForRent: 'Listings for rent',
};
const SLOGAN_PRESET_PLACEHOLDERS: Record<keyof BannerSlogans['presets'], string> = {
  activeListings: '“N active listings”',
  agents: '“N agents”',
  listingsForSale: '“N listings for sale”',
  listingsForRent: '“N listings for rent”',
};

function BannerSlogansEditor({
  value,
  onChange,
  propertiesType,
  mainFeed,
  showAgentFeed,
}: {
  value: BannerSlogans;
  onChange: (next: BannerSlogans) => void;
  propertiesType: ProptreeEnumValue | null;
  mainFeed: ProptreeEnumValue | null;
  showAgentFeed: ProptreeEnumValue | null;
}) {
  const presetKeys = Object.keys(value.presets) as Array<
    keyof BannerSlogans['presets']
  >;

  // A preset is "unavailable" when its underlying feature isn't
  // visible on the public page — the marquee would otherwise advertise
  // a number whose source the visitor can't see. We disable the toggle
  // (with a contextual hint) so the user can't turn on a preset that
  // would never render. Reasons stack: e.g. listings hidden + Type=Rent
  // disables listings-for-sale via two paths; the hint surfaces the
  // most specific one.
  const typeValue = propertiesType?.value ?? null;
  const listingsHidden = mainFeed?.value === 'disabled';
  const agentsHidden = showAgentFeed?.value === 'disabled';

  const reasonFor = (
    key: keyof BannerSlogans['presets'],
  ): string | null => {
    if (key === 'activeListings' && listingsHidden) {
      return 'n/a — Show listings is disabled';
    }
    if (key === 'agents' && agentsHidden) {
      return 'n/a — Show agents is disabled';
    }
    if (key === 'listingsForSale') {
      if (listingsHidden) return 'n/a — Show listings is disabled';
      if (typeValue === 'rent') return 'n/a — Type is set to Rentals only';
    }
    if (key === 'listingsForRent') {
      if (listingsHidden) return 'n/a — Show listings is disabled';
      if (typeValue === 'sale') return 'n/a — Type is set to Sale only';
    }
    return null;
  };

  const presetUnavailable: Record<keyof BannerSlogans['presets'], boolean> = {
    activeListings: reasonFor('activeListings') !== null,
    agents: reasonFor('agents') !== null,
    listingsForSale: reasonFor('listingsForSale') !== null,
    listingsForRent: reasonFor('listingsForRent') !== null,
  };

  const setPreset = (key: keyof BannerSlogans['presets'], v: boolean) => {
    if (presetUnavailable[key]) return;
    onChange({
      ...value,
      presets: { ...value.presets, [key]: v },
    });
  };

  const [addOpen, setAddOpen] = React.useState(false);
  const addCustom = (text: string) => {
    if (value.customs.length >= MAX_CUSTOM_SLOGANS) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    onChange({ ...value, customs: [...value.customs, trimmed] });
  };
  const updateCustom = (i: number, text: string) => {
    // Hard-clamp at the input layer so over-long pastes can't slip past
    // the per-row maxLength when the field is programmatically set.
    const clipped = text.slice(0, SLOGAN_CHAR_LIMIT);
    onChange({
      ...value,
      customs: value.customs.map((s, j) => (j === i ? clipped : s)),
    });
  };
  const removeCustom = (i: number) =>
    onChange({
      ...value,
      customs: value.customs.filter((_, j) => j !== i),
    });

  const customCount = value.customs.length;
  const remainingCustomSlots = MAX_CUSTOM_SLOGANS - customCount;

  return (
    <div className="mt-3 space-y-4">
      {/* Predefined slogans panel */}
      <div className="rounded-md border border-border bg-card">
        <div className="border-b border-[hsl(var(--hairline))] px-4 py-2">
          <div className="mono-label text-muted-foreground">
            Predefined slogans
          </div>
        </div>
        <div className="divide-y divide-[hsl(var(--hairline))]">
          {presetKeys.map((key) => {
            const reason = reasonFor(key);
            const unavailable = reason !== null;
            return (
              <div
                key={key}
                className={cn(
                  'flex items-center justify-between gap-3 px-4 py-2.5',
                  unavailable && 'opacity-50',
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-sans text-[13px] text-foreground">
                    {SLOGAN_PRESET_LABELS[key]}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {SLOGAN_PRESET_PLACEHOLDERS[key]}
                    <span aria-hidden className="px-1.5 opacity-60">
                      ·
                    </span>
                    {reason ?? 'only shows if more than one exist'}
                  </div>
                </div>
                <SwitchToggle
                  checked={!unavailable && value.presets[key]}
                  onChange={(v) => setPreset(key, v)}
                  ariaLabel={SLOGAN_PRESET_LABELS[key]}
                  disabled={unavailable}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom slogans panel */}
      <div className="rounded-md border border-border bg-card">
        <div className="flex items-center justify-between gap-3 border-b border-[hsl(var(--hairline))] px-4 py-2">
          <div className="mono-label text-muted-foreground">
            Custom slogans
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">
            {customCount} / {MAX_CUSTOM_SLOGANS}
          </div>
        </div>
        {value.customs.length > 0 && (
          <div className="divide-y divide-[hsl(var(--hairline))]">
            {value.customs.map((text, i) => (
              <CustomSloganRow
                key={i}
                value={text}
                onChange={(v) => updateCustom(i, v)}
                onRemove={() => removeCustom(i)}
              />
            ))}
          </div>
        )}
        <div
          className={cn(
            'px-4 py-2',
            value.customs.length > 0 &&
              'border-t border-[hsl(var(--hairline))]',
          )}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddOpen(true)}
            disabled={remainingCustomSlots <= 0}
          >
            <Plus />
            Add slogan
          </Button>
        </div>
      </div>

      <AddSloganModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={addCustom}
      />
    </div>
  );
}

// Modal for adding a single custom slogan. Mirrors the
// AddProptreeButtonModal scaffolding (backdrop, dialog card, escape +
// click-out close, body-scroll lock) so the page UX feels consistent
// across the two "add to a list" flows.
function AddSloganModal({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (text: string) => void;
}) {
  const [text, setText] = React.useState('');
  const [shown, setShown] = React.useState(open);
  const [visible, setVisible] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      setShown(true);
      setText('');
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      const r1 = requestAnimationFrame(() => {
        const r2 = requestAnimationFrame(() => {
          setVisible(true);
          inputRef.current?.focus();
        });
        return () => cancelAnimationFrame(r2);
      });
      return () => {
        cancelAnimationFrame(r1);
        document.body.style.overflow = prev;
        window.removeEventListener('keydown', onKey);
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!shown) return null;

  const trimmed = text.trim();
  const remaining = SLOGAN_CHAR_LIMIT - text.length;
  const submit = () => {
    if (!trimmed) return;
    onAdd(trimmed);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative w-full max-w-md overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="border-b border-[hsl(var(--hairline))] px-5 py-4">
          <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
            Add slogan
          </h2>
          <p className="mt-1.5 font-sans text-[12px] text-muted-foreground">
            Short tagline to rotate in the marquee. Up to{' '}
            {SLOGAN_CHAR_LIMIT} characters.
          </p>
        </div>
        <div className="px-5 py-5">
          <Label htmlFor="add-slogan-input">Slogan</Label>
          <Input
            id="add-slogan-input"
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, SLOGAN_CHAR_LIMIT))}
            maxLength={SLOGAN_CHAR_LIMIT}
            placeholder="e.g. Find your dream home"
            className="mt-1 bg-muted/30"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />
          <div className="mt-1.5 text-right">
            <span
              className={cn(
                'font-mono text-[10px] tabular-nums',
                remaining <= 10 ? 'text-amber-600' : 'text-muted-foreground',
              )}
            >
              {text.length}/{SLOGAN_CHAR_LIMIT}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[hsl(var(--hairline))] bg-muted/30 px-5 py-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={!trimmed}
          >
            Add slogan
          </Button>
        </div>
      </div>
    </div>
  );
}

// Single custom-slogan row: text input with a live char counter and a
// remove button. The counter goes amber as the user approaches the cap
// so they get a soft warning before the maxLength clamp kicks in.
function CustomSloganRow({
  value,
  onChange,
  onRemove,
}: {
  value: string;
  onChange: (v: string) => void;
  onRemove: () => void;
}) {
  const remaining = SLOGAN_CHAR_LIMIT - value.length;
  return (
    <div className="flex items-center gap-2 px-4 py-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={SLOGAN_CHAR_LIMIT}
        placeholder="e.g. Find your dream home"
        className="h-9 flex-1 bg-muted/30"
      />
      <span
        className={cn(
          'shrink-0 font-mono text-[10px] tabular-nums',
          remaining <= 10 ? 'text-amber-600' : 'text-muted-foreground',
        )}
      >
        {value.length}/{SLOGAN_CHAR_LIMIT}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label="Remove slogan"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
      >
        <Trash2 />
      </Button>
    </div>
  );
}

// Compact toggle without the surrounding "Enabled" label box that
// SwitchBox renders. Used inside list rows where each item already has
// its own label / description, so the boxed look would be redundant.
function SwitchToggle({
  checked,
  onChange,
  ariaLabel,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => {
        if (disabled) return;
        onChange(!checked);
      }}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
        disabled && 'cursor-not-allowed',
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
          checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
        )}
      />
    </button>
  );
}

// Hard limits for the custom marquee slogans. Per-string cap keeps
// scrolling content scannable; total cap stops the marquee from
// drowning real content. Mirrored on the public renderer so the API
// can enforce identical bounds when the save endpoint lands.
export const SLOGAN_CHAR_LIMIT = 60;
export const MAX_CUSTOM_SLOGANS = 10;

// Slogan list for the top marquee strip. Presets are stat-driven
// strings the public renderer composes from the live counts (e.g.
// "187 active listings"); customs are arbitrary user-authored taglines.
interface BannerSlogans {
  enabled: boolean;
  presets: {
    activeListings: boolean;
    agents: boolean;
    listingsForSale: boolean;
    listingsForRent: boolean;
  };
  customs: string[];
}

// Two render targets toggled via tabs in the manager:
//   - `top`  — top section banner (rotating slogan strip; configured
//              via `bannerSlogans`).
//   - `card` — between-listings banner card (configured via the legacy
//              `showAgencyBanner` / `agencyBannerStats` switches).
type SloganTab = 'top' | 'card';

const SLOGAN_TAB_LABELS: Record<SloganTab, string> = {
  top: 'Banner · Slogans',
  card: 'Banner · Agency',
};

function defaultBannerSlogans(): BannerSlogans {
  return {
    enabled: false,
    presets: {
      activeListings: false,
      agents: false,
      listingsForSale: false,
      listingsForRent: false,
    },
    customs: [],
  };
}

// Local form state — keys match the legacy `proptree.*` paths so wiring
// the eventual PATCH endpoint is a 1-1 mapping.
interface FormState {
  alias: string;
  profileFirstName: string;
  profileImage: string | null;
  externalAccess: ProptreeEnumValue | null;
  whatsAppCountryCode: ProptreeEnumValue | null;
  whatsAppNumber: string;
  showAgencyBanner: ProptreeEnumValue | null;
  agencyBannerStats: ProptreeEnumValue | null;
  bannerSlogans: BannerSlogans;
  showAgentFeed: ProptreeEnumValue | null;
  showAllAgencyListings: ProptreeEnumValue | null;
  mainFeed: ProptreeEnumValue | null;
  propertiesType: ProptreeEnumValue | null;
  property: ProptreeEnumValue | null;
  // Selected qualify-leads form. Stored as enum-shape so the existing
  // EnumSelect can render it (value = form _id).
  feed: ProptreeEnumValue | null;
  buttons: ProfileButton[];
  // Branded partner buttons that we offer pre-configured (Mortgage Finder
  // today, more later). Keyed by partner id; value is enabled/disabled.
  partners: Record<string, boolean>;
  // Controls the "Stay in the loop" newsletter signup card on the public
  // proptree page. Off until the manager opts in.
  newsletter: boolean;
  // Card copy, prefilled with the defaults when nothing is saved.
  // Blank saves as null → the public card shows the defaults.
  newsletterTitle: string;
  newsletterSubtitle: string;
}

function initialFormState(d: AgencyProptreeSettings): FormState {
  return {
    alias: d.alias ?? '',
    profileFirstName: d.profileFirstName ?? '',
    profileImage: d.profileImage,
    externalAccess: d.externalAccess,
    whatsAppCountryCode: d.whatsAppCountryCode,
    whatsAppNumber: d.whatsAppNumber ?? '',
    // Pass the saved enum-shaped switches through as-is. Unset reads
    // as `null` so the toggle starts off — nothing is implicitly
    // enabled until the manager flips it.
    showAgencyBanner: d.agency?.showAgencyBanner ?? null,
    agencyBannerStats: d.agency?.agencyBannerStats ?? null,
    bannerSlogans: d.bannerSlogans ?? defaultBannerSlogans(),
    showAgentFeed: d.agency?.showAgentFeed ?? null,
    showAllAgencyListings: d.showAllAgencyListings,
    // Legacy stores `null` when the field has never been written, but
    // visually the listings feed is on by default. Default to "Enabled"
    // so the switch reflects the public-page reality.
    mainFeed: d.mainFeed ?? MAIN_FEED_OPTIONS[0] ?? null,
    propertiesType: d.propertiesType,
    property: d.property,
    feed: d.feed
      ? { id: 1, name: d.feed.name, value: d.feed.id }
      : null,
    buttons: d.buttons ?? [],
    // No partners enabled until the manager opts in per-partner.
    partners: {},
    newsletter: d.newsletter === true,
    newsletterTitle: d.newsletterTitle ?? NEWSLETTER_DEFAULT_TITLE,
    newsletterSubtitle: d.newsletterSubtitle ?? NEWSLETTER_DEFAULT_SUBTITLE,
  };
}

// Inline status text shown next to the Check availability button.
// Colored by state — destructive for taken/invalid/error, emerald for
// saved, muted for idle/in-flight.
function AliasCheckStatus({ state }: { state: AliasCheckState }) {
  if (state.kind === 'idle') return null;
  const muted = state.kind === 'checking' || state.kind === 'saving';
  const taken =
    state.kind === 'taken' || state.kind === 'invalid' || state.kind === 'error';
  const saved = state.kind === 'saved';
  const text =
    state.kind === 'checking'
      ? 'Checking…'
      : state.kind === 'saving'
        ? 'Available — saving…'
        : state.kind === 'taken'
          ? 'Already in use — pick another shortname.'
          : state.kind === 'invalid'
            ? state.message
            : state.kind === 'error'
              ? state.message
              : 'Available — saved.';
  return (
    <span
      className={cn(
        'font-sans text-[12px]',
        muted && 'text-muted-foreground',
        taken && 'text-destructive',
        saved && 'text-emerald-600',
      )}
    >
      {text}
    </span>
  );
}

// Local state machine for the alias availability check + inline-save
// flow on the Identity card. Mirrors the affiliate page's pattern
// verbatim so the two surfaces feel identical to the manager.
type AliasCheckState =
  | { kind: 'idle' }
  | { kind: 'invalid'; message: string }
  | { kind: 'checking' }
  | { kind: 'taken' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

function SettingsForm({
  form,
  set,
  data,
  formOptions,
  onAliasSaved,
}: {
  form: FormState;
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  data: AgencyProptreeSettings;
  formOptions: ProptreeEnumValue[];
  // Fired when the Check availability button successfully inline-saves
  // the alias. The page snaps `original.alias` to the saved value so
  // the dirty diff sees alias as clean without disturbing other fields.
  onAliasSaved: (savedAlias: string) => void;
}) {
  const externalEnabled = form.externalAccess?.value === 'enabled';
  const mainFeedEnabled = form.mainFeed?.value !== 'disabled';
  const displayName =
    data.profileCompany || data.profileFirstName || data.alias || '—';

  const [aliasCheck, setAliasCheck] = React.useState<AliasCheckState>({
    kind: 'idle',
  });
  // Reset the status whenever the user types — the previous result no
  // longer applies to the new value.
  React.useEffect(() => {
    setAliasCheck({ kind: 'idle' });
  }, [form.alias]);

  const checkAndSaveAlias = async () => {
    const value = form.alias.trim();
    if (value === '') {
      setAliasCheck({ kind: 'invalid', message: 'Type a shortname first.' });
      return;
    }
    if (!/^[a-z0-9-]+$/.test(value)) {
      setAliasCheck({
        kind: 'invalid',
        message:
          'Only lowercase letters, digits, and dashes are allowed — no spaces.',
      });
      return;
    }
    setAliasCheck({ kind: 'checking' });
    try {
      const { available } = await client.proptrees.checkAffiliateAlias({
        alias: value,
        excludeId: data._id,
      });
      if (!available) {
        setAliasCheck({ kind: 'taken' });
        return;
      }
      setAliasCheck({ kind: 'saving' });
      await client.proptrees.updateAgencyAlias(value);
      onAliasSaved(value);
      setAliasCheck({ kind: 'saved' });
    } catch (err) {
      setAliasCheck({ kind: 'error', message: (err as Error).message });
    }
  };

  const canCheck =
    form.alias.trim() !== '' &&
    aliasCheck.kind !== 'checking' &&
    aliasCheck.kind !== 'saving';

  return (
    <div className="space-y-4">
      <SectionCard
        title="Identity"
        subtitle="Image, custom URL, and tagline"
        icon={UserIcon}
        defaultOpen
      >
      {/* Identity row */}
      <div className="grid grid-cols-12 gap-4 p-5">
        <div className="col-span-12">
          <Label>Image</Label>
          <div className="mt-1 flex items-center gap-3">
            <ProfileAvatar image={form.profileImage} name={displayName} />
            <ImageUploadButton
              kind="proptree-profile"
              proptreeId={data._id}
              onUploaded={(url) => set('profileImage', url)}
              label={form.profileImage ? 'Replace image' : 'Upload image'}
            />
          </div>
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Profile picture shown at the top of your agency proptree page.
          </p>
        </div>

        <div className="col-span-12">
          <Label htmlFor="alias">
            Custom shortname{' '}
            <span className="ml-1 font-mono text-[10px] text-muted-foreground">
              (recommended)
            </span>
          </Label>
          <div className="mt-1 flex">
            <span className="inline-flex h-11 items-center rounded-l-md border border-r-0 border-border bg-muted px-3 font-mono text-[13px] text-muted-foreground">
              https://proptr.ee/
            </span>
            <Input
              id="alias"
              value={form.alias}
              onChange={(e) => set('alias', e.target.value)}
              placeholder={data.shortId ?? ''}
              className="rounded-l-none bg-muted/30"
            />
          </div>
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            The path under proptr.ee/… that opens your agency page.
            Lowercase letters, numbers, and dashes only.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={checkAndSaveAlias}
              disabled={!canCheck}
            >
              {aliasCheck.kind === 'checking'
                ? 'Checking…'
                : aliasCheck.kind === 'saving'
                  ? 'Saving…'
                  : 'Check availability'}
            </Button>
            <AliasCheckStatus state={aliasCheck} />
          </div>
        </div>
      </div>

      {/* Tagline */}
      <div className="grid grid-cols-12 gap-4 border-t border-[hsl(var(--hairline))]/40 p-5">
        <div className="col-span-12">
          <Label htmlFor="tagline">Tagline</Label>
          <Input
            id="tagline"
            className="mt-1 bg-muted/30"
            value={form.profileFirstName}
            onChange={(e) => set('profileFirstName', e.target.value)}
          />
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Short line shown beneath the agency name on the public page.
          </p>
        </div>
      </div>

      </SectionCard>

      <SectionCard
        title="Buttons"
        subtitle="Call-to-action and partner buttons"
        icon={MousePointerClick}
      >
      {/* Buttons */}
      <ButtonsSection
        buttons={form.buttons}
        onChange={(next) => set('buttons', next)}
        formOptions={formOptions}
        partners={form.partners}
        onPartnersChange={(next) => set('partners', next)}
        proptreeId={data._id}
      />
      </SectionCard>

      <SectionCard
        title="Listings"
        subtitle="Feed filters and lead-capture form"
        icon={Home}
      >
      {/* Listing filters */}
      <div className="p-5">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 sm:col-span-6">
            <Label>Show listings</Label>
            <SwitchBox
              checked={form.mainFeed?.value !== 'disabled'}
              onChange={(v) =>
                set(
                  'mainFeed',
                  MAIN_FEED_OPTIONS.find(
                    (o) => o.value === (v ? 'enabled' : 'disabled'),
                  ) ??
                    MAIN_FEED_OPTIONS[0] ??
                    null,
                )
              }
              ariaLabel="Show listings"
            />
            <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
              Renders the listings feed on the agency page.
            </p>
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Label>Show agents</Label>
            <SwitchBox
              checked={form.showAgentFeed?.value === 'enabled'}
              onChange={(v) =>
                set(
                  'showAgentFeed',
                  AGENT_FEED_OPTIONS.find(
                    (o) => o.value === (v ? 'enabled' : 'disabled'),
                  ) ?? null,
                )
              }
              ariaLabel="Show agents"
            />
            <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
              Bottom marquee that lists every agent in the brokerage.
            </p>
          </div>
          {mainFeedEnabled && (
            <>
              <div className="col-span-12 sm:col-span-6 sm:col-start-1">
                <Label>Type</Label>
                <EnumSelect
                  className="mt-1 bg-muted/30"
                  options={PROPERTIES_TYPE_OPTIONS}
                  value={form.propertiesType}
                  onChange={(v) => set('propertiesType', v)}
                  placeholder="Click to Select"
                />
                <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Restrict the feed to sale-only, rental-only, or both.
                </p>
              </div>
              <div
                className={cn(
                  'col-span-12 sm:col-span-6',
                  !form.propertiesType && 'opacity-50',
                )}
              >
                <Label>Plan</Label>
                <EnumSelect
                  className="mt-1 bg-muted/30"
                  options={PROPERTY_PLAN_OPTIONS}
                  value={form.property}
                  onChange={(v) => set('property', v)}
                  placeholder="Click to Select"
                  disabled={!form.propertiesType}
                />
                <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Restrict the feed to on-plan, off-plan, or both.
                </p>
              </div>
              <div className="col-span-12 sm:col-span-6 sm:col-start-1">
                <Label>Qualify leads (listing clicks)</Label>
                <EnumSelect
                  className="mt-1 bg-muted/30"
                  options={formOptions}
                  value={form.feed}
                  onChange={(v) => set('feed', v)}
                  placeholder="Select a form"
                />
                <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Form shown to visitors before their listing click reaches
                  the agent.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      </SectionCard>

      <SectionCard
        title="Banners"
        subtitle="Top marquee and agency banner card"
        icon={Megaphone}
      >
      {/* Marquee slogans — controls two distinct banner surfaces (top
          strip + between-listings card). Tabs flip between the two
          configs; each surface has its own enable switch + presets +
          customs. The legacy "Show agency banner" / "Banner stats"
          controls live inside the card tab — they only apply to that
          surface. */}
      <MarqueeSection
        value={form.bannerSlogans}
        onChange={(next) => set('bannerSlogans', next)}
        showAgencyBanner={form.showAgencyBanner}
        agencyBannerStats={form.agencyBannerStats}
        onShowAgencyBannerChange={(v) => set('showAgencyBanner', v)}
        onAgencyBannerStatsChange={(v) => set('agencyBannerStats', v)}
        propertiesType={form.propertiesType}
        mainFeed={form.mainFeed}
        showAgentFeed={form.showAgentFeed}
      />
      </SectionCard>

      <SectionCard
        title="Newsletter"
        subtitle="Stay in the loop email signup"
        icon={Mail}
      >
      <NewsletterSection
        enabled={form.newsletter}
        title={form.newsletterTitle}
        subtitle={form.newsletterSubtitle}
        onEnabledChange={(v) => set('newsletter', v)}
        onTitleChange={(v) => set('newsletterTitle', v)}
        onSubtitleChange={(v) => set('newsletterSubtitle', v)}
      />
      </SectionCard>

      <SectionCard
        title="External access"
        subtitle="Self-serve login via WhatsApp"
        icon={KeyRound}
      >
      {/* External access / Country code / WhatsApp */}
      <div className="grid grid-cols-12 gap-4 p-5">
        <div className="col-span-12 sm:col-span-6">
          <Label>External access</Label>
          {/* Grants a real sign-in to the proptree — locked in test mode,
              where the seeded agency has no account behind it. */}
          {isTestMode() ? (
            <Tooltip
              label="Can't enable external access in test mode"
              className="block w-full cursor-not-allowed"
            >
              <div className="w-full">
                <SwitchBox
                  checked={form.externalAccess?.value === 'enabled'}
                  onChange={() => undefined}
                  ariaLabel="External access"
                  disabled
                />
              </div>
            </Tooltip>
          ) : (
            <SwitchBox
              checked={form.externalAccess?.value === 'enabled'}
              onChange={(v) =>
                set(
                  'externalAccess',
                  EXTERNAL_ACCESS_OPTIONS.find(
                    (o) => o.value === (v ? 'enabled' : 'disabled'),
                  ) ?? null,
                )
              }
              ariaLabel="External access"
            />
          )}
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Lets the agency owner sign in to the agency proptree like a
            regular proptree user, using the WhatsApp number below.
          </p>
        </div>
        <div
          className={cn(
            'col-span-12 sm:col-span-6 sm:col-start-1',
            !externalEnabled && 'opacity-50',
          )}
        >
          <Label>Country code</Label>
          <EnumSelect
            className="mt-1 bg-muted/30"
            options={COUNTRY_CODES}
            value={form.whatsAppCountryCode}
            onChange={(v) => set('whatsAppCountryCode', v)}
            placeholder="Click to Select"
            disabled={!externalEnabled}
          />
        </div>
        <div
          className={cn(
            'col-span-12 sm:col-span-6',
            !externalEnabled && 'opacity-50',
          )}
        >
          <Label htmlFor="whatsapp">
            WhatsApp{' '}
            <span className="ml-1 font-mono text-[10px] text-muted-foreground">
              (used for login)
            </span>
          </Label>
          <Input
            id="whatsapp"
            className="mt-1 bg-muted/30"
            value={form.whatsAppNumber}
            onChange={(e) => set('whatsAppNumber', e.target.value)}
            disabled={!externalEnabled}
          />
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Number the agency owner uses to sign in to the agency
            proptree.
          </p>
        </div>
      </div>
      </SectionCard>
    </div>
  );
}

// In dev, point the iframe at the local landing app (port 4040) so the
// preview renders something the developer can actually see. Override
// with VITE_PUBLIC_URL when you want a different target (staging, etc).
const PUBLIC_URL =
  (import.meta.env.VITE_PUBLIC_URL as string | undefined) ??
  (import.meta.env.DEV ? 'http://localhost:4040' : 'https://proptr.ee');

// Compact card shown next to the page title — opens the public agency
// proptree page in a new tab. Mirrors the `SettingsCard` shape on the
// proptree list page (300px wide, title + subtitle + button on the right)
// so the header reads as a coherent set of agency entry points.
function OpenProptreeCard({ alias }: { alias: string }) {
  const url = `${PUBLIC_URL}/${alias}`;
  const href = withDemoFlag(url);
  return (
    <Card className="flex w-[300px] items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-sans text-[13px] font-semibold leading-tight text-foreground">
          Your proptree
        </div>
        <p className="mt-1 truncate font-sans text-[11px] leading-snug text-muted-foreground">
          {url.replace(/^https?:\/\//, '')}
        </p>
      </div>
      <Button asChild size="sm" variant="outline">
        <a href={href} target="_blank" rel="noopener noreferrer">
          <ExternalLink />
          Open
        </a>
      </Button>
    </Card>
  );
}

type Viewport = 'mobile' | 'desktop';
// Width is fixed per viewport so the iframe renders at a real device
// width; height is computed to fill the available preview area at the
// same scale, so the iframe always feels like the full window height.
const VIEWPORT_WIDTH: Record<Viewport, number> = {
  mobile: 390,
  desktop: 1280,
};

// Shape of the postMessage payload the landing page expects. Mirrors
// `ProptreePreviewPatch` in apps/landing/app/[slug]/ProptreeBody.tsx —
// kept in sync by hand. Every field is optional; fields that aren't
// represented in the manager form (e.g. enterprise color) are omitted.
interface ProptreePreviewPatch {
  name?: string | null;
  image?: string | null;
  buttons?: Array<{
    id: string;
    title: string;
    subtitle: string | null;
    url: string;
    icon: string | null;
    image: string | null;
    images: string[] | null;
    type: string;
  }>;
  agencyBanner?: { visible?: boolean; showStats?: boolean };
  bannerSlogans?: BannerSlogans;
  showAgents?: boolean;
  mainFeed?: boolean;
  // Listings filters surfaced to the public page so the preview
  // iframe's listings feed reflects unsaved Type / Plan changes.
  propertiesType?: string | null;
  property?: string | null;
  partners?: Record<string, boolean>;
  newsletter?: boolean;
  newsletterTitle?: string | null;
  newsletterSubtitle?: string | null;
}

// Build a preview patch from the manager's live form state. Only the
// fields with a clear public-page mapping are projected here; everything
// else (enterprise colour, agent feed list, etc.) stays whatever the
// API last served. Disabled buttons are filtered out so the preview
// matches what the public page would render after a save.
// Strips empty / whitespace-only customs from a BannerSlogans before it
// goes over the wire so the marquee never renders blank rotations
// while the user is mid-typing a new entry.
function cleanBannerSlogans(config: BannerSlogans): BannerSlogans {
  return {
    enabled: config.enabled,
    presets: { ...config.presets },
    customs: config.customs.map((s) => s.trim()).filter((s) => s.length > 0),
  };
}

function buildPreviewPatch(form: FormState): ProptreePreviewPatch {
  return {
    name: form.profileFirstName || null,
    image: form.profileImage,
    buttons: form.buttons
      .filter((b) => b.enabled)
      .map((b) => ({
        id: b.id,
        title: b.title,
        subtitle: b.subtitle,
        url: b.url,
        icon: b.icon,
        image: b.image,
        images: b.images,
        type: b.type,
      })),
    agencyBanner: {
      visible: form.showAgencyBanner?.value === 'enabled',
      showStats: form.agencyBannerStats?.value === 'enabled',
    },
    bannerSlogans: cleanBannerSlogans(form.bannerSlogans),
    showAgents: form.showAgentFeed?.value === 'enabled',
    mainFeed: form.mainFeed?.value !== 'disabled',
    // ProptreeEnumValue.value is `string | number | boolean`; these two
    // API fields are string-only. Every option list here uses string
    // values, so a non-string means the field was never set.
    propertiesType:
      typeof form.propertiesType?.value === 'string'
        ? form.propertiesType.value
        : null,
    property:
      typeof form.property?.value === 'string' ? form.property.value : null,
    partners: form.partners,
    newsletter: form.newsletter,
    // Blank → null so the preview shows the default copy, same as a save.
    newsletterTitle: form.newsletterTitle.trim() || null,
    newsletterSubtitle: form.newsletterSubtitle.trim() || null,
  };
}

// Live iframe preview of the public proptree page. The src has
// `?preview=1` appended so the landing app activates its postMessage
// listener; the parent (this component) posts a `ProptreePreviewPatch`
// to the iframe whenever the form changes, which the landing body
// merges over the server-fetched data so the developer sees unsaved
// edits without refreshing.
function ProptreePreview({
  data,
  form,
}: {
  data: AgencyProptreeSettings;
  form: FormState;
}) {
  const slug = data.alias || form.alias || data.shortId || '';
  const baseUrl = slug ? `${PUBLIC_URL}/${slug}` : null;
  const url = baseUrl ? withDemoFlag(`${baseUrl}?preview=1`) : null;
  const [viewport, setViewport] = React.useState<Viewport>('desktop');
  const patch = React.useMemo(() => buildPreviewPatch(form), [form]);

  return (
    <Card className="flex h-full min-h-[640px] flex-col overflow-hidden p-0 lg:min-h-0">
      <div className="relative flex shrink-0 items-center justify-center border-b border-[hsl(var(--hairline))] px-5 py-3">
        <span className="mono-label absolute left-5 text-muted-foreground">
          Preview
        </span>
        <ViewportToggle value={viewport} onChange={setViewport} />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden bg-muted/40">
        {url ? (
          <ScaledFrame key={slug} url={url} viewport={viewport} patch={patch} />
        ) : (
          <div className="flex h-full items-center justify-center px-6 text-center font-mono text-[11px] text-muted-foreground">
            Set an alias to preview the public proptree.
          </div>
        )}
      </div>
    </Card>
  );
}

// Drives the cross-origin postMessage bridge to the preview iframe.
// Re-posts the patch whenever it changes, and immediately re-posts
// whenever the iframe announces it has (re)mounted via
// `proptr:preview-ready` — that handshake covers initial load and any
// internal navigation inside the iframe. targetOrigin is intentionally
// `*` because the iframe origin may differ from the manager's (in dev:
// 4040 vs 5175; in prod: proptr.ee vs app.proptr.ee). The payload is
// non-sensitive form state, not credentials.
function usePreviewBridge(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  patch: ProptreePreviewPatch,
) {
  // Keep the latest patch in a ref so the long-lived ready-handler
  // closure always sees the current value without forcing the listener
  // to re-bind on every render.
  const patchRef = React.useRef(patch);
  React.useEffect(() => {
    patchRef.current = patch;
  }, [patch]);

  // Re-post the patch whenever it changes. Iframe may not be ready yet
  // on the first call — that's fine, the ready-handler below will
  // re-post once the iframe announces itself.
  React.useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'proptr:preview', patch },
      '*',
    );
  }, [iframeRef, patch]);

  // Listen once for the iframe's ready handshake. Whenever it fires
  // (initial mount, internal navigation), re-post the latest patch.
  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const d = e.data;
      if (d && typeof d === 'object' && d.type === 'proptr:preview-ready') {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'proptr:preview', patch: patchRef.current },
          '*',
        );
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [iframeRef]);
}

function ViewportToggle({
  value,
  onChange,
}: {
  value: Viewport;
  onChange: (v: Viewport) => void;
}) {
  return (
    <div className="inline-flex shrink-0 rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
      {(['mobile', 'desktop'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cn(
            'rounded px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.06em] transition-colors',
            value === v
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

// Mobile mode just fills the preview area at native size — no scaling
// needed. Desktop renders at a real desktop width (1280px) and is scaled
// down via CSS transform to fit the available width, with the iframe
// height computed so the rendered page fills the full preview height.
// Both branches forward the live `patch` to a shared bridge hook that
// posts it into the iframe via postMessage.
function ScaledFrame({
  url,
  viewport,
  patch,
}: {
  url: string;
  viewport: Viewport;
  patch: ProptreePreviewPatch;
}) {
  if (viewport === 'mobile') {
    return <MobileFrame url={url} patch={patch} />;
  }
  return <DesktopScaledFrame url={url} patch={patch} />;
}

function MobileFrame({
  url,
  patch,
}: {
  url: string;
  patch: ProptreePreviewPatch;
}) {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  usePreviewBridge(iframeRef, patch);
  return (
    <iframe
      ref={iframeRef}
      src={url}
      title="Proptree preview"
      className="h-full w-full border-0 bg-background"
    />
  );
}

function DesktopScaledFrame({
  url,
  patch,
}: {
  url: string;
  patch: ProptreePreviewPatch;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [size, setSize] = React.useState({ scale: 1, nativeH: 800 });
  const nativeW = VIEWPORT_WIDTH.desktop;
  usePreviewBridge(iframeRef, patch);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      const scale = Math.min(w / nativeW, 1);
      const nativeH = scale > 0 ? h / scale : h;
      setSize({ scale, nativeH });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [nativeW]);

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full items-start justify-center overflow-hidden"
    >
      <div
        style={{
          width: nativeW * size.scale,
          height: size.nativeH * size.scale,
        }}
      >
        <iframe
          ref={iframeRef}
          src={url}
          title="Proptree preview"
          style={{
            width: nativeW,
            height: size.nativeH,
            transform: `scale(${size.scale})`,
            transformOrigin: 'top left',
          }}
          className="border-0 bg-background"
        />
      </div>
    </div>
  );
}

// Lists the existing buttons attached to the agency proptree and exposes
// a "Create new button" CTA that opens the type picker. Inline-edit the
// title / subtitle / URL on each card; remove via the trash icon. Save
// flow isn't wired yet at the page level, so changes live in form state
// until a `PUT /agency` endpoint lands.
function ButtonsSection({
  buttons,
  onChange,
  formOptions,
  partners,
  onPartnersChange,
  proptreeId,
}: {
  buttons: ProfileButton[];
  onChange: (next: ProfileButton[]) => void;
  formOptions: ProptreeEnumValue[];
  partners: Record<string, boolean>;
  onPartnersChange: (next: Record<string, boolean>) => void;
  // Owning agency-default proptree id — threaded to row + modal so
  // button image uploads land under that proptree's Spaces prefix.
  proptreeId: string;
}) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [draggingIndex, setDraggingIndex] = React.useState<number | null>(null);
  const [tab, setTab] = React.useState<'personal' | 'partners'>('personal');

  const updateAt = (i: number, patch: Partial<ProfileButton>) =>
    onChange(buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const removeAt = (i: number) =>
    onChange(buttons.filter((_, j) => j !== i));

  const handleDrop = (target: number) => {
    if (draggingIndex == null || draggingIndex === target) {
      setDraggingIndex(null);
      return;
    }
    const next = [...buttons];
    const [moved] = next.splice(draggingIndex, 1);
    // splice returns `T[]`, so the destructured element is `T | undefined`.
    if (moved) {
      next.splice(target, 0, moved);
      onChange(next);
    }
    setDraggingIndex(null);
  };

  return (
    <div className="p-5">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <div className="inline-flex rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
            <SectionTabButton
              active={tab === 'personal'}
              onClick={() => setTab('personal')}
            >
              Personal buttons
            </SectionTabButton>
            <SectionTabButton
              active={tab === 'partners'}
              onClick={() => setTab('partners')}
            >
              Partner buttons
            </SectionTabButton>
          </div>
          <Label className="mt-3 block">Buttons</Label>

          {tab === 'personal' ? (
            <>
              {buttons.length === 0 ? (
                <div className="mt-1 rounded-md border border-dashed border-border bg-card/40 p-6 text-center font-sans text-[12px] text-muted-foreground">
                  No buttons yet.
                </div>
              ) : (
                <div className="mt-1 space-y-2">
                  {buttons.map((b, i) => (
                    <ProptreeButtonRow
                      key={b.id}
                      index={i}
                      button={b}
                      isDragging={draggingIndex === i}
                      formOptions={formOptions}
                      proptreeId={proptreeId}
                      onChange={(patch) => updateAt(i, patch)}
                      onRemove={() => removeAt(i)}
                      onDragStart={() => setDraggingIndex(i)}
                      onDragEnd={() => setDraggingIndex(null)}
                      onDrop={() => handleDrop(i)}
                    />
                  ))}
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3 bg-muted hover:bg-muted-foreground/20"
                onClick={() => setModalOpen(true)}
                disabled={buttons.length >= 20}
              >
                <Plus />
                Create new button
              </Button>
              <AddProptreeButtonModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onAdd={(button) => onChange([...buttons, button])}
                formOptions={formOptions}
                proptreeId={proptreeId}
              />
            </>
          ) : (
            <PartnerToggleList
              value={partners}
              onChange={onPartnersChange}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function SectionTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors',
        active
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}


// A row-shaped toggle that visually matches an EnumSelect input box —
// label on the left ("Enabled"/"Disabled"), switch on the right. Used
// in place of a 2-option dropdown when on/off is the only choice.
function SwitchBox({
  checked,
  onChange,
  ariaLabel,
  disabled = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <div
      className={cn(
        'mt-1 flex h-11 items-center justify-between rounded-md border border-border bg-muted/30 px-3',
        disabled && 'opacity-60',
      )}
    >
      <span className="font-sans text-[13px] text-foreground">
        {checked ? 'Enabled' : 'Disabled'}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
          checked ? 'bg-primary' : 'bg-muted-foreground/30',
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
            checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
          )}
        />
      </button>
    </div>
  );
}

// Adapter from legacy `{ id, name, value }` shape to our generic Select.
// `value` is the string we round-trip; we re-attach the original tuple
// (with id/name) on change so legacy-shaped saves can ship unchanged.
function EnumSelect({
  options,
  value,
  onChange,
  placeholder = 'Click to Select',
  disabled,
  className,
}: {
  options: ProptreeEnumValue[];
  value: ProptreeEnumValue | null;
  onChange: (v: ProptreeEnumValue) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const items = React.useMemo<SelectOption<string>[]>(
    () =>
      options
        .filter((o) => o.value != null)
        .map((o) => ({ value: String(o.value), label: o.name ?? '—' })),
    [options],
  );
  const current = value?.value != null ? String(value.value) : null;
  // Skip search on tiny lists — toggles like Enabled/Disabled don't need it.
  const searchable = options.length > 5;

  return (
    <Select<string>
      options={items}
      value={current}
      onChange={(next) => {
        const orig = options.find(
          (o) => o.value != null && String(o.value) === next,
        );
        if (orig) onChange(orig);
      }}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      searchable={searchable}
    />
  );
}

function ProfileAvatar({
  image,
  name,
  size = 48,
}: {
  image: string | null;
  name: string;
  size?: number;
}) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="shrink-0 rounded-full bg-white object-contain p-1 ring-1 ring-border"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full bg-muted font-mono font-bold uppercase text-foreground"
      style={{ width: size, height: size, fontSize: Math.round(size / 4) }}
    >
      {initials(name) || '—'}
    </div>
  );
}
